import {
  ChatMessage,
  ChatModelMode,
  ChatSource,
  ChatStreamChunk
} from '../entity';
import {
  getDecodedToken,
  getStoredToken,
  redirectToLogin
} from '../../utils/auth';

interface ConsumeChatStreamHandlers {
  appendToLastAssistant: (content: string, sources?: ChatSource[]) => void;
  replaceLastAssistant: (content: string, sources?: ChatSource[]) => void;
  setStatus: (status: string) => void;
}

interface StreamChatOptions {
  endpointChat: string;
  endpointAgent: string;
  threadId: string;
  agentId: string;
  messages: ChatMessage[];
  conversationSummary: string;
  webAccessEnabled: boolean;
  modelMode: ChatModelMode;
  documentFiles?: File[];
  onThreadId?: (threadId: string) => void;
}

interface WarmUpOptions {
  endpointChat: string;
  threadId: string;
  agentId: string;
  modelMode: ChatModelMode;
  wait: boolean;
}

interface ParsedStreamLine {
  chunk?: ChatStreamChunk;
  terminal: boolean;
}

export class ChatStreamingService {
  private activeAbortController?: AbortController;

  private warmupPromise?: Promise<void>;

  private lastWarmupKey = '';

  private readonly openTimeoutMs = 30 * 1000;

  private readonly inactivityTimeoutMs = 120 * 1000;

  isActiveRequest(): boolean {
    return Boolean(this.activeAbortController);
  }

  cancelActiveRequest() {
    this.activeAbortController?.abort();
  }

  async warmUp(options: WarmUpOptions): Promise<void> {
    const normalizedThreadId = String(options.threadId || '').trim();

    if (!normalizedThreadId) {
      return;
    }

    const warmupKey = [
      normalizedThreadId,
      options.agentId,
      options.modelMode,
      options.wait ? 'wait' : 'start'
    ].join(':');

    if (
      !options.wait &&
      this.lastWarmupKey === warmupKey &&
      this.warmupPromise
    ) {
      return this.warmupPromise;
    }

    let headers: Headers;

    try {
      headers = this.buildStreamHeaders();
    } catch {
      return;
    }

    const url = `${options.endpointChat}/warmup/${encodeURIComponent(
      normalizedThreadId
    )}/${encodeURIComponent(options.agentId)}`;
    const promise = fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model_mode: options.modelMode,
        wait: options.wait
      })
    })
      .then((response) => {
        if (response.status === 401 || response.status === 422) {
          redirectToLogin();
          throw new Error('Bitte erneut anmelden.');
        }

        if (!response.ok) {
          throw new Error(
            `Chat-Warmup konnte nicht gestartet werden (${response.status})`
          );
        }
      })
      .catch((error) => {
        console.debug('Chat-Warmup konnte nicht abgeschlossen werden:', error);
      });

    this.lastWarmupKey = warmupKey;
    this.warmupPromise = promise;
    return promise;
  }

  async consume(
    responseStream: AsyncGenerator<ChatStreamChunk>,
    handlers: ConsumeChatStreamHandlers
  ) {
    let pendingContent = '';
    let pendingSources: ChatSource[] | undefined;
    let flushAnimationFrame: number | undefined;
    let flushPromise: Promise<void> | undefined;

    const nextAnimationFrame = () =>
      new Promise<void>((resolve) => {
        flushAnimationFrame = window.requestAnimationFrame(() => {
          flushAnimationFrame = undefined;
          resolve();
        });
      });

    const getFrameContentLength = (contentLength: number) => {
      if (contentLength > 2000) {
        return Math.ceil(contentLength / 6);
      }

      if (contentLength > 800) {
        return 120;
      }

      if (contentLength > 320) {
        return 64;
      }

      if (contentLength > 120) {
        return 28;
      }

      return 8;
    };

    const flushNextFrame = () => {
      if (pendingContent) {
        const contentLength = getFrameContentLength(pendingContent.length);
        const content = pendingContent.slice(0, contentLength);
        pendingContent = pendingContent.slice(content.length);
        const sources = pendingContent ? undefined : pendingSources;

        if (!pendingContent) {
          pendingSources = undefined;
        }

        handlers.appendToLastAssistant(content, sources);
        return;
      }

      if (pendingSources) {
        const sources = pendingSources;
        pendingSources = undefined;
        handlers.appendToLastAssistant('', sources);
      }
    };

    const startFlushLoop = () => {
      if (flushPromise) {
        return flushPromise;
      }

      flushPromise = (async () => {
        try {
          while (pendingContent || pendingSources) {
            await nextAnimationFrame();
            flushNextFrame();
          }
        } finally {
          flushPromise = undefined;
        }
      })();

      return flushPromise;
    };

    const clearPending = () => {
      pendingContent = '';
      pendingSources = undefined;
    };

    const finishPending = async () => {
      if (!pendingContent && !pendingSources) {
        return;
      }

      await startFlushLoop();
    };

    try {
      for await (const chunk of responseStream) {
        if (typeof chunk !== 'string' && chunk.status) {
          handlers.setStatus(chunk.status);
          continue;
        }

        const content = typeof chunk === 'string' ? chunk : chunk.content;
        const sources = typeof chunk === 'string' ? undefined : chunk.sources;

        if (typeof chunk !== 'string' && chunk.replace) {
          clearPending();
          handlers.replaceLastAssistant(content, sources);
          continue;
        }

        pendingContent += content;

        if (sources) {
          pendingSources = sources;
        }

        startFlushLoop();
      }
    } finally {
      await finishPending();

      if (flushAnimationFrame !== undefined) {
        window.cancelAnimationFrame(flushAnimationFrame);
        flushAnimationFrame = undefined;
      }
    }
  }

  async *streamChat(
    options: StreamChatOptions
  ): AsyncGenerator<ChatStreamChunk> {
    const url = `${options.endpointChat}/${options.threadId}/${options.agentId}`;
    const abortController = this.startRequest();

    try {
      const response = await this.openChatStream(url, abortController, {
        messages: options.messages,
        conversation_summary: options.conversationSummary,
        web_access_enabled: options.webAccessEnabled,
        model_mode: options.modelMode
      }, options.documentFiles);
      yield* this.readJsonLineStream(
        response,
        abortController,
        options.modelMode === 'images'
          ? 5 * 60 * 1000
          : this.inactivityTimeoutMs,
        options.onThreadId
      );
    } finally {
      this.finishRequest(abortController);
    }
  }

  async *streamAgentChat(
    options: StreamChatOptions
  ): AsyncGenerator<ChatStreamChunk> {
    const url = `${options.endpointAgent}/chat/${options.agentId}/${options.threadId}`;
    const abortController = this.startRequest();

    try {
      const response = await this.openChatStream(url, abortController, {
        messages: options.messages,
        conversation_summary: options.conversationSummary,
        web_access_enabled: options.webAccessEnabled,
        model_mode: options.modelMode
      });
      yield* this.readJsonLineStream(
        response,
        abortController,
        this.inactivityTimeoutMs,
        options.onThreadId
      );
    } finally {
      this.finishRequest(abortController);
    }
  }

  private startRequest(): AbortController {
    const abortController = new AbortController();
    this.activeAbortController?.abort();
    this.activeAbortController = abortController;
    return abortController;
  }

  private finishRequest(abortController: AbortController) {
    if (this.activeAbortController === abortController) {
      this.activeAbortController = undefined;
    }
  }

  private buildStreamHeaders(includeJsonContentType = true) {
    if (!getDecodedToken()) {
      redirectToLogin();
      throw new Error('Bitte erneut anmelden.');
    }

    const token = getStoredToken();
    const headers = new Headers();

    if (includeJsonContentType) {
      headers.set('Content-Type', 'application/json');
    }

    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private async openChatStream(
    url: string,
    abortController: AbortController,
    body: unknown,
    documentFiles?: File[]
  ) {
    const timeout = window.setTimeout(
      () => abortController.abort(),
      this.openTimeoutMs
    );

    try {
      const hasDocuments = Boolean(documentFiles?.length);
      const requestBody = hasDocuments
        ? this.createMultipartChatBody(body, documentFiles || [])
        : JSON.stringify(body);
      const response = await fetch(url, {
        method: 'POST',
        headers: this.buildStreamHeaders(!hasDocuments),
        signal: abortController.signal,
        body: requestBody
      });

      if (response.status === 401 || response.status === 422) {
        redirectToLogin();
        throw new Error('Bitte erneut anmelden.');
      }

      if (!response.ok || !response.body) {
        throw new Error(await this.streamErrorMessage(response));
      }

      return response as Response & { body: ReadableStream<Uint8Array> };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private createMultipartChatBody(body: unknown, files: File[]): FormData {
    const formData = new FormData();
    formData.set('payload', JSON.stringify(body));
    files.forEach((file) => formData.append('files', file, file.name));
    return formData;
  }

  private async streamErrorMessage(response: Response): Promise<string> {
    try {
      const payload = await response.json();
      if (payload && typeof payload.msg === 'string') {
        return payload.msg;
      }
    } catch {
      // A non-JSON response still gets the concise fallback below.
    }

    return `Stream konnte nicht geöffnet werden (${response.status})`;
  }

  private async readChatStreamChunk(
    reader: ReadableStreamDefaultReader<string>,
    abortController: AbortController,
    timeoutMs = this.inactivityTimeoutMs
  ) {
    let timeout: number | undefined;
    type StreamReadResult = Awaited<
      ReturnType<ReadableStreamDefaultReader<string>['read']>
    >;
    const timeoutPromise = new Promise<StreamReadResult>((_, reject) => {
      timeout = window.setTimeout(() => {
        abortController.abort();
        reject(
          new Error('Der Chat-Stream hat zu lange keine Antwort geliefert.')
        );
      }, timeoutMs);
    });

    try {
      return await Promise.race([reader.read(), timeoutPromise]);
    } finally {
      if (timeout !== undefined) {
        window.clearTimeout(timeout);
      }
    }
  }

  private async *readJsonLineStream(
    response: Response & { body: ReadableStream<Uint8Array> },
    abortController: AbortController,
    timeoutMs = this.inactivityTimeoutMs,
    onThreadId?: (threadId: string) => void
  ): AsyncGenerator<ChatStreamChunk> {
    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();
    let partial = '';
    let terminalEventSeen = false;

    while (true) {
      const { value, done } = await this.readChatStreamChunk(
        reader,
        abortController,
        timeoutMs
      );
      if (done) {
        break;
      }
      if (!value) continue;

      partial += value;

      const lines = partial
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '');
      partial = partial.endsWith('\n') ? '' : lines.pop() ?? '';

      for (const line of lines) {
        if (line === '[DONE]') {
          terminalEventSeen = true;
          continue;
        }

        const parsed = this.parseStreamLine(line, onThreadId);
        terminalEventSeen ||= parsed.terminal;
        if (parsed.chunk) {
          yield parsed.chunk;
        }
      }
    }

    const remainingLine = partial.trim();
    if (remainingLine === '[DONE]') {
      terminalEventSeen = true;
    } else if (remainingLine) {
      const parsed = this.parseStreamLine(remainingLine, onThreadId);
      terminalEventSeen ||= parsed.terminal;
      if (parsed.chunk) {
        yield parsed.chunk;
      }
    }

    if (!terminalEventSeen) {
      throw new Error(
        'Der Chat-Stream wurde unerwartet beendet. Bitte versuche es erneut.'
      );
    }
  }

  private parseStreamLine(
    line: string,
    onThreadId?: (threadId: string) => void
  ): ParsedStreamLine {
    try {
      const json = JSON.parse(line);

      if (json.thread_id) {
        onThreadId?.(json.thread_id);
      }

      return {
        chunk: this.getChatStreamChunk(json),
        terminal: json?.event === 'final' || json?.event === 'done'
      };
    } catch (error) {
      console.error('Fehler beim Parsen des Chunks:', error, line);
      return { terminal: false };
    }
  }

  private normalizeChatSources(sources: unknown): ChatSource[] | undefined {
    if (!Array.isArray(sources)) {
      return undefined;
    }

    return sources.filter((source): source is ChatSource => {
      if (!source || typeof source !== 'object') {
        return false;
      }

      const candidate = source as ChatSource;
      return (
        typeof candidate.id === 'number' &&
        (candidate.type === 'web' ||
          candidate.type === 'file' ||
          candidate.type === 'news') &&
        typeof candidate.title === 'string'
      );
    });
  }

  private getChatStreamChunk(json: any): ChatStreamChunk | undefined {
    if (json?.timing) {
      return undefined;
    }

    const sources = this.normalizeChatSources(json?.sources);
    const replace = json?.replace === true || json?.event === 'final';
    const delta = json?.content?.choices?.[0]?.delta?.content;
    const status = typeof json?.status === 'string' ? json.status : undefined;
    const error = typeof json?.error === 'string' ? json.error : undefined;

    if (status) {
      return { content: '', sources, replace, status };
    }

    if (error) {
      return {
        content: `\n${error}`,
        sources,
        replace: false,
        status: undefined
      };
    }

    if (delta != null) {
      return { content: delta, sources, replace };
    }

    if (typeof json?.content === 'string') {
      return { content: json.content, sources, replace };
    }

    if (typeof json?.message === 'string') {
      return { content: json.message, sources, replace };
    }

    if (sources?.length) {
      return { content: '', sources, replace };
    }

    return undefined;
  }
}
